import React, { useCallback, useEffect, useState } from 'react';
import { GitMerge, Loader2, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { productsAPI } from '../services/api';
import { toast } from 'sonner';
import CompatPendingList from './CompatPendingList';

// Admin › Produits : compatibilités véhicules héritées des pièces équivalentes (références partagées / liens manuels)
const CompatSyncCard = ({ onChanged, onEdit }) => {
  const [st, setSt] = useState(null);
  const load = useCallback(() => productsAPI.compatSyncStatus().then(setSt).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const running = !!st?.job?.running;
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(async () => {
      try {
        const s = await productsAPI.compatSyncStatus();
        setSt(s);
        if (!s.job?.running) { clearInterval(id); toast.success(`Compatibilités synchronisées : ${s.job?.updated || 0} fiche(s) complétée(s) sur ${s.job?.total || 0}`); onChanged?.(); }
      } catch { /* retry */ }
    }, 2500);
    return () => clearInterval(id);
  }, [running, onChanged]);

  const start = async () => {
    try {
      const r = await productsAPI.compatSyncAll();
      if (!r.started) toast.info(r.message);
      setSt(s => ({ ...s, job: r.job }));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Lancement impossible'); }
  };

  const [mailing, setMailing] = useState(false);
  const sendDigest = async () => {
    setMailing(true);
    try { const r = await productsAPI.compatDigest(); toast.success(r.sent ? `Récap envoyé à l'admin : ${r.pending} fiche(s) en attente, ${r.conflicts} incohérence(s)` : (r.reason || r.skipped || 'Rien à envoyer')); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Envoi impossible'); }
    setMailing(false);
  };
  if (!st) return null;
  const job = st.job || {};
  return (
    <div className="bg-white rounded-lg shadow-sm p-4 mb-6" data-testid="compat-sync-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h3 className="font-semibold text-gray-900 flex items-center gap-2"><GitMerge size={16} className="text-[#1e3a5f]" /> Compatibilités héritées des pièces équivalentes</h3>
        <p className="text-xs text-gray-500" data-testid="compat-sync-summary">
          Les marques, modèles et motorisations des pièces équivalentes (autres marques, références partagées) sont ajoutés automatiquement à chaque enregistrement. Chaque lundi, un e-mail récapitule les fiches sensibles en attente et les incohérences.
          {' '}{st.synced_products} fiche(s) complétée(s) à ce jour{st.pending_products ? ` · ${st.pending_products} en attente de validation` : ''}
          {running && <span className="ml-2 text-orange-600 font-medium" data-testid="compat-sync-progress">· en cours {job.done}/{job.total} ({job.updated} complétée(s))</span>}
          {!running && job.finished_at && <span className="ml-2 text-gray-400">· dernier passage : {job.updated}/{job.total} fiche(s) complétée(s)</span>}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
      <Button size="sm" variant="outline" onClick={sendDigest} disabled={mailing} data-testid="compat-digest-send" title="Envoyer maintenant à l'admin le récap des fiches en attente et des incohérences">
        {mailing ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Mail size={14} className="mr-1" />} Envoyer le récap
      </Button>
      <Button size="sm" onClick={start} disabled={running} data-testid="compat-sync-start" className="bg-[#1e3a5f] hover:bg-[#162c48] text-white" title="Repasse sur tout le catalogue : chaque fiche hérite des compatibilités de ses équivalences">
        {running ? <Loader2 size={14} className="mr-1 animate-spin" /> : <GitMerge size={14} className="mr-1" />} Synchroniser tout le catalogue
      </Button>
      </div>
      </div>
      <CompatPendingList refreshKey={`${st.pending_products}-${job.finished_at || ''}`} onChanged={() => { load(); onChanged?.(); }} onEdit={onEdit} />
    </div>
  );
};

export default CompatSyncCard;
